/**
 * The current UI locale and the loaded dictionaries.
 *
 * - `en` is always available (its strings live in the namespaces themselves).
 * - Any other locale is one lazy chunk (`locales/<id>/index.ts`), loaded
 *   before the locale becomes current — the UI never shows half a language.
 * - The choice is cached in localStorage so the next cold start paints in the
 *   right language before `/api/me` answers; the profile (`users.locale`) is
 *   the cross-device source of truth (see `profile-sync.ts`).
 */
import { DEFAULT_LOCALE, INTL_TAG, isLocale, type Locale } from './config';
import type { Messages } from './types';

type Bundle = Readonly<Record<string, Messages>>;

const loaders = import.meta.glob<{ default: Bundle }>('./locales/*/index.ts');

function loaderFor(locale: Locale): (() => Promise<{ default: Bundle }>) | undefined {
  return loaders[`./locales/${locale}/index.ts`];
}

const bundles = new Map<Locale, Bundle>();
let current: Locale = DEFAULT_LOCALE;
const listeners = new Set<() => void>();

export const LOCALE_STORAGE_KEY = 'pj-locale';

export function getLocale(): Locale {
  return current;
}

/** The `Intl` tag of the current locale ("ru-RU"). */
export function getIntlLocale(): string {
  return INTL_TAG[current];
}

/** The loaded dictionary of `ns` in the current locale (undefined for EN or a missing ns). */
export function getBundleNs(ns: string, locale: Locale = current): Messages | undefined {
  return bundles.get(locale)?.[ns];
}

export function subscribeLocale(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function loadLocale(locale: Locale): Promise<void> {
  if (locale === DEFAULT_LOCALE || bundles.has(locale)) return;
  const load = loaderFor(locale);
  if (!load) return;
  const mod = await load();
  bundles.set(locale, mod.default);
}

/** Test / boot helper: install a bundle without the dynamic import. */
export function registerBundle(locale: Locale, bundle: Bundle): void {
  bundles.set(locale, bundle);
}

function readStored(): Locale | null {
  try {
    const v = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
}

function writeStored(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* private mode — the profile still remembers it */
  }
}

/** The locale cached from the last session, if any. */
export function storedLocale(): Locale | null {
  return readStored();
}

/**
 * Make `locale` current: load its chunk first, then flip and notify. A
 * failed load (offline, stale chunk) keeps the current language.
 */
export async function setLocale(locale: Locale, opts: { persist?: boolean } = {}): Promise<boolean> {
  try {
    await loadLocale(locale);
  } catch (err) {
    console.warn('[i18n] could not load locale', locale, err);
    return false;
  }
  if (opts.persist !== false) writeStored(locale);
  if (locale === current) return true;
  current = locale;
  if (typeof document !== 'undefined') document.documentElement.lang = INTL_TAG[locale];
  for (const fn of [...listeners]) fn();
  return true;
}
